import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Page } from '../../components';
import DhobaraPageLayout from '../../components/DhobaraPageLayout/DhobaraPageLayout';
import { ALL_DESIGNERS, slugify } from '../../data/designers';
import css from './DesignersPage.module.css';

const DesignersPage = () => {
  const grouped = useMemo(() => {
    const sorted = [...new Set(ALL_DESIGNERS)].sort((a, b) =>
      a.localeCompare(b, 'en', { sensitivity: 'base' })
    );
    const map = {};
    sorted.forEach(name => {
      const letter = name[0].toUpperCase();
      if (!map[letter]) map[letter] = [];
      map[letter].push(name);
    });
    return map;
  }, []);

  const letters = Object.keys(grouped).sort();

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'ItemList',
        name: 'South Asian Designers on Dhobara',
        description: 'Browse 200+ South Asian luxury designers available on Dhobara pre-owned marketplace.',
        url: 'https://dhobara.com/designers',
        numberOfItems: ALL_DESIGNERS.length,
        itemListElement: ALL_DESIGNERS.sort().map((name, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name,
          url: `https://dhobara.com/designer/${slugify(name)}`,
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://dhobara.com' },
          { '@type': 'ListItem', position: 2, name: 'Designers', item: 'https://dhobara.com/designers' },
        ],
      },
    ],
  };

  return (
    <Page
      title="Designers We Accept | 200+ South Asian Designer Brands on Dhobara"
      description="Browse the 200+ South Asian designers accepted on Dhobara, from Sabyasachi to Élan to Manish Malhotra. Buy or sell pre-owned designer wear with confidence."
      scrollingDisabled={false}
    >
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
    <DhobaraPageLayout>
      <div className={css.pageHeader}>
        <span className={css.eyebrow}>Curated Excellence</span>
        <h1 className={css.pageTitle}>The <em>Designers</em> We Accept</h1>
        <p className={css.pageSubtitle}>
          We accept over 200 South Asian designers — from Karachi to Delhi to New York. Browse the full list below.
        </p>
        <div className={css.anchorNav}>
          {letters.map(l => (
            <a key={l} href={`#letter-${l}`} className={css.anchorBtn}>{l}</a>
          ))}
        </div>
      </div>

      <div className={css.content}>
        {letters.map(letter => (
          <div key={letter} id={`letter-${letter}`} className={css.letterGroup}>
            <div className={css.letterHeading}>{letter}</div>
            <ul className={css.designerList}>
              {grouped[letter].map(name => {
                const slug = slugify(name);
                return (
                  <li key={name} className={css.designerItem}>
                    <Link to={`/designer/${slug}`} className={css.designerLink}>
                      {name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <div className={css.noteSection}>
        <p className={css.noteText}>
          Don't see your designer? <Link to="/contact">Get in touch</Link> — we may still be able to accommodate your piece. Our accepted designer list is updated regularly.
        </p>
      </div>
    </DhobaraPageLayout>
    </Page>
  );
};

export default DesignersPage;
